import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem, GlowButton } from "../../../components/animations/AnimatedElements";

const solutions = [
  {
    num: "01",
    title: "고객 현장 분석 · ADX 컨설팅",
    desc: "고객의 현장 환경을 직접 방문·분석하고 ADX 플랫폼 기반의 최적 도입 전략을 제시합니다. AI·디지털트윈이 실질적으로 해결할 수 있는 문제를 명확히 정의하고 ROI 중심의 컨설팅을 제공합니다.",
    icon: "ri-search-eye-line",
    stats: [{ label: "접근 방식", val: "현장 중심" }, { label: "결과물", val: "도입 전략서" }],
  },
  {
    num: "02",
    title: "ADX 플랫폼 설계 구축",
    desc: "컨설팅 결과를 기반으로 영상 AI, 객체 인식, 이상 감지, 자동화 알림 등 고객 맞춤형 AI Agent 솔루션을 설계하고 직접 구축합니다. CCTV부터 산업 센서까지 다양한 데이터 소스와 연동합니다.",
    icon: "ri-brain-line",
    stats: [{ label: "AI 기술", val: "Agent 기반" }, { label: "운영", val: "24/7 자동화" }],
  },
  {
    num: "03",
    title: "Digital Twin 구현 · 연동",
    desc: "Unreal Engine 및 Unity 기반으로 고객 현장을 3D 디지털트윈으로 구현합니다. AI Agent 데이터와 실시간 연동하여 현실 공간의 상황을 가상에서 모니터링·시뮬레이션할 수 있는 환경을 제공합니다.",
    icon: "ri-cube-line",
    stats: [{ label: "엔진", val: "Unreal/Unity" }, { label: "연동", val: "AI Agent 실시간" }],
  },
  {
    num: "04",
    title: "운영 고도화 · 지속 성장",
    desc: "구축 후에도 지속적인 모니터링과 데이터 분석을 통해 솔루션을 고도화합니다. 새로운 AI 기술과 디지털트윈 업데이트를 반영하여 고객의 플랫폼이 항상 최신 상태를 유지하도록 지원합니다.",
    icon: "ri-line-chart-line",
    stats: [{ label: "지원", val: "지속 고도화" }, { label: "관리", val: "통합 운영" }],
  },
];

const techStack = ["ADX Platform", "AI Agent", "Computer Vision", "Deep Learning", "Digital Twin", "Unreal Engine", "Unity", "Real-time Analytics", "CCTV Integration", "API Gateway", "Cloud Platform", "Data Intelligence"];

export default function SolutionsSection() {
  const [active, setActive] = useState(0);

  return (
    <section id="solutions" className="py-28 bg-brand-dark relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/4 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">ADX Platform Approach</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-4">
            <h2 className="section-title">
              솔루션 <span className="text-cyan-400">소개</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-md">
              CITY-AI ADX 플랫폼의 접근 방식 — 컨설팅에서 시작해 AI·디지털트윈 솔루션 구축, 운영 고도화까지 전 과정을 함께합니다.
            </p>
          </div>

          {/* ADX Pipeline Visual */}
          <div className="bg-brand-darkCard border border-cyan-900/30 rounded-lg p-6 mb-10">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">ADX 플랫폼 접근 파이프라인</p>
            <div 
              className="hidden lg:flex items-center justify-between relative"
              role="tablist"
              aria-label="솔루션 단계 선택"
            >
              <div className="absolute top-5 left-12 right-12 h-px bg-gradient-to-r from-cyan-500/40 via-teal-500/40 to-cyan-500/40" />
              {solutions.map((sol, idx) => (
                <motion.button
                  key={sol.num}
                  role="tab"
                  aria-selected={active === idx}
                  aria-controls={`solution-panel-${idx}`}
                  onClick={() => setActive(idx)}
                  className={`relative z-10 flex flex-col items-center gap-3 cursor-pointer transition-all duration-200 ${active === idx ? "scale-110" : "hover:scale-105"}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className={`w-12 h-12 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${
                      active === idx ? "border-cyan-500 bg-cyan-500/20 shadow-glow-sm" : "border-cyan-900/50 bg-brand-dark"
                    }`}
                    animate={active === idx ? { boxShadow: "0 0 20px rgba(0, 229, 255, 0.3)" } : {}}
                  >
                    <i className={`${sol.icon} text-lg ${active === idx ? "text-cyan-400" : "text-gray-500"}`} />
                  </motion.div>
                  <span className={`text-xs font-bold tracking-wider ${active === idx ? "text-cyan-400" : "text-gray-600"}`}>
                    {sol.num}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Active Solution Detail */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                id={`solution-panel-${active}`}
                role="tabpanel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-brand-darkCard border border-cyan-900/30 rounded-lg p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    className="icon-box-lg"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <i className={`${solutions[active].icon} text-cyan-400 text-2xl`} />
                  </motion.div>
                  <div>
                    <span className="text-cyan-500 text-xs font-bold tracking-widest">{solutions[active].num}</span>
                    <h3 className="text-white text-xl font-bold mt-0.5">{solutions[active].title}</h3>
                  </div>
                </div>
                <p className="text-gray-400 leading-relaxed text-base mb-8">{solutions[active].desc}</p>
                <div className="grid grid-cols-2 gap-4">
                  {solutions[active].stats.map((stat, idx) => (
                    <motion.div
                      key={stat.label}
                      className="border border-cyan-900/30 rounded-lg p-4"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <div className="text-cyan-400 font-bold text-lg">{stat.val}</div>
                      <div className="text-gray-500 text-xs mt-1">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Solution List */}
            <StaggerContainer className="space-y-3" staggerDelay={0.05}>
              {solutions.map((sol, idx) => (
                <StaggerItem key={sol.num}>
                  <motion.div
                    onClick={() => setActive(idx)}
                    role="button"
                    tabIndex={0}
                    aria-label={`${sol.num} ${sol.title} 선택`}
                    onKeyDown={(e) => e.key === 'Enter' && setActive(idx)}
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                      active === idx
                        ? "border-cyan-500/50 bg-cyan-500/5"
                        : "border-cyan-900/20 bg-brand-darkCard hover:border-cyan-900/50"
                    }`}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.div
                      className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        active === idx ? "bg-cyan-500/20" : "bg-brand-dark"
                      }`}
                      animate={active === idx ? { scale: 1.1 } : { scale: 1 }}
                    >
                      <i className={`${sol.icon} text-base ${active === idx ? "text-cyan-400" : "text-gray-500"}`} />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xs font-bold ${active === idx ? "text-cyan-500" : "text-gray-600"}`}>
                          {sol.num}
                        </span>
                        <span className={`text-sm font-semibold ${active === idx ? "text-white" : "text-gray-400"}`}>
                          {sol.title}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs truncate">{sol.desc.substring(0, 55)}...</p>
                    </div>
                    <motion.div
                      className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                      animate={active === idx ? { x: 5 } : { x: 0 }}
                    >
                      <i className={`ri-arrow-right-s-line ${active === idx ? "text-cyan-400" : "text-gray-600"}`} />
                    </motion.div>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>

          {/* Tech Stack */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 border border-cyan-900/20 rounded-lg p-6 bg-brand-darkCard"
          >
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">ADX 플랫폼 기술 스택</p>
            <StaggerContainer className="flex flex-wrap gap-3" staggerDelay={0.05}>
              {techStack.map((tech) => (
                <StaggerItem key={tech}>
                  <motion.span
                    className="badge cursor-default"
                    whileHover={{ scale: 1.05, borderColor: "rgba(0, 229, 255, 0.4)" }}
                  >
                    {tech}
                  </motion.span>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}
